import { Injectable } from '@angular/core';
import { safeSessionStorage } from 'src/app/safe-web-storage';
import { ClientStore } from 'src/app/stores/stores';
import {
  WIDGET_ROUTE_STATES,
  WIDGET_ROUTE_VIEWS,
  isWidgetRouteAvailable,
  startPageRoute,
} from '../utils/WidgetPageUtils';

export type InitialViewType =
  | 'VIDEO'
  | 'CONSUMPTION_MODE'
  | 'PROMOTION'
  | 'CATALOG';

export interface ViewDecision {
  viewType: InitialViewType;
  clearEverything: boolean;
  routePath: string | null;
}

/**
 * Where a bottom-bar template app stands with its start page, the first tab of
 * its bar (story #11856), in this app launch:
 * - `PENDING`: not reached yet.
 * - `OPENING`: on its way to the start page; the launch views wait for it.
 * - `OPENED`: the start page took the Menu's place, or the launch reached a
 *   page of its own first (a tab the customer tapped, an app link), so the
 *   launch views that belong to the Menu (the promotion pop-up) wait for the
 *   first Menu view.
 * - `SKIPPED`: a promotion push opened its pop-up, or the store cannot show
 *   the first tab. The launch goes on as in a Menu-first app.
 */
export type StartPageStatus = 'PENDING' | 'OPENING' | 'OPENED' | 'SKIPPED';

@Injectable({
  providedIn: 'root',
})
export class InitialViewOrchestratorService {
  private readonly STORAGE_PREFIX = 'initialView';
  /**
   * App-wide, not per store, so it lasts the whole launch whatever store the
   * customer switches to (a branch switch changes the store alias). Kept in
   * session storage like the other launch flags here: that lives exactly as
   * long as the app process, so a cold start opens on the start page again,
   * while a reload of the page mid-session (the store header's back arrow, a
   * failed confirmation's "back to home") does not.
   */
  private readonly START_PAGE_STATUS_KEY = `${this.STORAGE_PREFIX}:startPage`;
  private startPageStatus: StartPageStatus = this.readStartPageStatus();

  constructor() {}

  /**
   * Determines which view to show on initial load or reorder
   * @param store The current store
   * @param isReorder Whether this is a reorder flow (user completed an order and wants to order again)
   * @returns ViewDecision object with view type and routing information
   */
  determineInitialView(
    store: ClientStore | null,
    isReorder: boolean = false,
    currentOrderHasConsumptionMode?: boolean,
  ): ViewDecision {
    if (!store) {
      return this.createDecision('CATALOG', false, null);
    }

    // N4-M9 (card #9664): the per-alias `consumptionModeSelected` sessionStorage
    // flag is NOT a reliable gate on its own. When a previous customer's order
    // tear-down failed (the kiosk stayed on the catalog screen after that order
    // went SUBMITTED), the flag survived into the next customer's session, and a
    // fresh order was created without ever showing the selector — persisting an
    // order with no `properties.CONSUMPTION_MODE`. So when the caller positively
    // knows the *current* order has no consumption mode set
    // (`currentOrderHasConsumptionMode === false`), force the selector regardless
    // of the stale flag. `undefined` means "unknown" and preserves prior
    // flag-only behavior.
    const orderKnownMissingMode = currentOrderHasConsumptionMode === false;

    // First time visit flow
    if (!isReorder) {
      // Check video first (only shown on first visit)
      const hasVideo = this.hasVideoEnabled(store);
      const videoShown = this.isVideoShown(store.aliasName);

      if (hasVideo && !videoShown) {
        return this.createDecision('VIDEO', false, this.getVideoRoute(store));
      }

      // Check consumption mode
      const hasConsumption = this.hasConsumptionModeEnabled(store);
      const consumptionSelected = this.isConsumptionModeSelected(
        store.aliasName,
      );

      if (hasConsumption && (!consumptionSelected || orderKnownMissingMode)) {
        return this.createDecision(
          'CONSUMPTION_MODE',
          true,
          'consumption-mode',
        );
      }

      // Check promotion
      const hasPromotion = this.hasPromotionEnabled(store);
      const promotionShown = this.isPromotionShown(store.aliasName);

      if (hasPromotion && !promotionShown) {
        return this.createDecision('PROMOTION', true, 'promotion');
      }

      // Default to catalog
      return this.createDecision('CATALOG', false, null);
    }

    // Reorder flow - skip video, show consumption mode or promotion
    const hasConsumption = this.hasConsumptionModeEnabled(store);
    const hasPromotion = this.hasPromotionEnabled(store);
    const consumptionSelected = this.isConsumptionModeSelected(store.aliasName);

    // In reorder flow, if consumption mode is enabled but not yet selected
    // (or the current order positively lacks one), show it
    if (hasConsumption && (!consumptionSelected || orderKnownMissingMode)) {
      return this.createDecision('CONSUMPTION_MODE', true, 'consumption-mode');
    }

    // If consumption mode was already selected (or not needed), check for promotion
    if (hasPromotion) {
      return this.createDecision('PROMOTION', true, 'promotion');
    }

    // Default to catalog (clear basket for fresh start)
    return this.createDecision('CATALOG', true, null);
  }

  /**
   * The `loadView` fragment of the start page to open in place of `decision`
   * (story #11856), or `null` to go ahead with `decision`. The start page only
   * takes the Menu's place at launch: a catalog or promotion decision, since
   * the promotion pop-up belongs to the Menu, never the video or the Eat-In /
   * Take-Away question, never a reorder, and once per app launch.
   */
  startPageInsteadOf(
    decision: ViewDecision,
    isReorder: boolean,
  ): string | null {
    if (isReorder || this.startPageStatus !== 'PENDING') {
      return null;
    }
    if (decision.viewType !== 'CATALOG' && decision.viewType !== 'PROMOTION') {
      return null;
    }
    const route = startPageRoute();
    return route === null ? null : WIDGET_ROUTE_VIEWS[route];
  }

  /** The view state the start page renders, or `null` when there is none. */
  startPageViewState(): string | null {
    const route = startPageRoute();
    return route === null ? null : WIDGET_ROUTE_STATES[route];
  }

  getStartPageStatus(): StartPageStatus {
    return this.startPageStatus;
  }

  /**
   * `startPageInsteadOf`, claiming the start page for this launch when there
   * is one: the caller opens it (`LocationService.openWidgetPage`) and the
   * launch views wait for it from here on.
   *
   * Only claimed for a loaded `store` that can show the page, by the same rule
   * the bar and `LocationService.loadView` apply (`isWidgetRouteAvailable`).
   * A first tab the store cannot show, such as a brochure on a store without
   * one, gives up the start page for this launch, which then goes on exactly
   * as in an app whose first tab is the Menu, rather than claiming a page that
   * would never render.
   */
  takeStartPage(
    decision: ViewDecision,
    isReorder: boolean,
    store: ClientStore | null,
  ): string | null {
    const startPage = this.startPageInsteadOf(decision, isReorder);
    const route = startPageRoute();
    if (startPage === null || route === null || !store?.id) {
      return null;
    }
    if (!isWidgetRouteAvailable(route, store)) {
      this.setStartPageStatus('SKIPPED');
      return null;
    }
    this.setStartPageStatus('OPENING');
    return startPage;
  }

  /**
   * The `loadView` fragment of the start page while the app is on its way to
   * it, or `null`. The launch video's exit renders this instead of the Menu,
   * so no Menu frame shows between the video and the start page.
   */
  openingStartPage(): string | null {
    const route = startPageRoute();
    if (this.startPageStatus !== 'OPENING' || route === null) {
      return null;
    }
    return WIDGET_ROUTE_VIEWS[route];
  }

  markStartPageOpened(): void {
    if (this.startPageStatus === 'OPENING') {
      this.setStartPageStatus('OPENED');
    }
  }

  /**
   * The launch is on a page the start page must not replace: the customer
   * tapped a tab, an app link opened a page of its own, or the launch reached
   * the start page through the login's `?next=`, or another page, before the
   * start page was claimed or rendered.
   * The start page is then done for this launch and the Menu's launch views
   * wait for the Menu. Only in an app that has a start page.
   */
  settleStartPage(): void {
    if (
      startPageRoute() !== null &&
      (this.startPageStatus === 'PENDING' || this.startPageStatus === 'OPENING')
    ) {
      this.setStartPageStatus('OPENED');
    }
  }

  /**
   * A promotion push tap opened its pop-up, which the start page must not
   * replace. Only a launch whose start page is not claimed yet (PENDING) is
   * affected, and only in an app that has a start page.
   */
  skipStartPage(): void {
    if (startPageRoute() !== null && this.startPageStatus === 'PENDING') {
      this.setStartPageStatus('SKIPPED');
    }
  }

  private setStartPageStatus(status: StartPageStatus): void {
    this.startPageStatus = status;
    safeSessionStorage.setItem(this.START_PAGE_STATUS_KEY, status);
  }

  /**
   * A launch that was on its way to the start page when the page reloaded
   * has taken it already, so it counts as opened.
   */
  private readStartPageStatus(): StartPageStatus {
    const stored = safeSessionStorage.getItem(this.START_PAGE_STATUS_KEY);
    switch (stored) {
      case 'OPENING':
      case 'OPENED':
        return 'OPENED';
      case 'SKIPPED':
        return 'SKIPPED';
      default:
        return 'PENDING';
    }
  }

  /**
   * Check if store has video enabled
   */
  hasVideoEnabled(store: ClientStore): boolean {
    // Check if store has inactivity video URL configured
    const hasInactivityVideo = !!(
      store?.settings?.INACTIVITY_VIDEO &&
      store?.settings?.INACTIVITY_VIDEO.trim().length > 0
    );
    // INACTIVITY_OPTION can be boolean true or string 'ENABLED'
    const hasInactivityEnabled =
      store?.settings?.INACTIVITY_OPTION === 'ENABLED' ||
      store?.settings?.INACTIVITY_OPTION === true;

    return hasInactivityVideo && hasInactivityEnabled;
  }

  /**
   * Check if store has consumption mode (eat-in/takeaway) enabled
   */
  hasConsumptionModeEnabled(store: ClientStore): boolean {
    const deliveryDisabled = !store?.settings?.DELIVERY_ADDRESS;
    const askConsumptionMode =
      store?.settings?.ORDER_CAPTURE_ASK_EAT_IN_TAKEAWAY;
    return deliveryDisabled && askConsumptionMode;
  }

  /**
   * Check if store has active promotion
   */
  hasPromotionEnabled(store: ClientStore): boolean {
    return !!(
      store?.promotion?.name && store?.promotion?.name.trim().length > 0
    );
  }

  /**
   * Get video route based on store settings
   */
  getVideoRoute(store: ClientStore): string | null {
    // Return the kiosk video route
    return 'kiosk-video';
  }

  /**
   * Check if video has been shown in current session
   */
  isVideoShown(storeAlias: string): boolean {
    return (
      safeSessionStorage.getItem(
        `${this.STORAGE_PREFIX}:videoShown:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Check if consumption mode has been selected
   */
  isConsumptionModeSelected(storeAlias: string): boolean {
    return (
      safeSessionStorage.getItem(
        `${this.STORAGE_PREFIX}:consumptionModeSelected:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Check if promotion has been shown in current session
   */
  isPromotionShown(storeAlias: string): boolean {
    return (
      safeSessionStorage.getItem(
        `${this.STORAGE_PREFIX}:promotionShown:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Mark video as shown
   */
  markVideoAsShown(storeAlias: string): void {
    safeSessionStorage.setItem(
      `${this.STORAGE_PREFIX}:videoShown:${storeAlias}`,
      'true',
    );
  }

  /**
   * Mark consumption mode as selected
   */
  markConsumptionModeAsSelected(storeAlias: string): void {
    safeSessionStorage.setItem(
      `${this.STORAGE_PREFIX}:consumptionModeSelected:${storeAlias}`,
      'true',
    );
  }

  /**
   * Mark promotion as shown
   */
  markPromotionAsShown(storeAlias: string): void {
    safeSessionStorage.setItem(
      `${this.STORAGE_PREFIX}:promotionShown:${storeAlias}`,
      'true',
    );
  }

  /**
   * Check if user is in reorder flow
   */
  isReorderFlow(storeAlias: string): boolean {
    return (
      safeSessionStorage.getItem(
        `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Set reorder flow flag
   * Note: In reorder flow, we keep consumption/promotion states as-is
   * (if already shown, don't show again; if not shown yet, show them)
   */
  setReorderFlow(storeAlias: string, isReorder: boolean): void {
    if (isReorder) {
      safeSessionStorage.setItem(
        `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
        'true',
      );
      // Don't clear consumption/promotion states - keep them as they are
      // This way, if user already saw them, they won't see them again
    } else {
      safeSessionStorage.removeItem(
        `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
      );
    }
  }

  /**
   * Clear all reorder state (called after order completion or session end)
   */
  clearReorderState(storeAlias: string): void {
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
    );
  }

  /**
   * Check if the store has the Eat-In/Take-Away pickup-only configuration
   * (ORDER_CAPTURE_ASK_EAT_IN_TAKEAWAY enabled AND no delivery-to-address).
   * Equivalent to the predicate that `hasConsumptionModeEnabled` already
   * encodes — exposed under its own name so callers that care about the
   * "default deliveryMethod must be NO_LOCATION" rule do not need to
   * re-derive it from `store.settings`. Returns false when `store` is
   * null/undefined.
   */
  isEatInTakeAwayPickupOnly(store: ClientStore | null): boolean {
    return this.hasConsumptionModeEnabled(store);
  }

  /**
   * Persist the "this store is Eat-In/Take-Away pickup-only" verdict for the
   * current browser session so race-prone callers (notably
   * `OrderService.getInitOrder()`) can resolve the default `deliveryMethod`
   * without waiting on the async `selectedStore` Observable to have emitted.
   *
   * Set this once per session, as soon as the store's settings load
   * successfully and the predicate evaluates true. Do not unset on inactivity
   * / kiosk-video / thank-you-page restarts — the same physical store still
   * has the same configuration. Only `clearAllState()` (real session end)
   * clears it.
   */
  markEatInTakeAwayPickupOnly(storeAlias: string): void {
    if (!storeAlias) {
      return;
    }
    safeSessionStorage.setItem(
      `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:${storeAlias}`,
      'true',
    );
  }

  /**
   * Read the "Eat-In/Take-Away pickup-only" sessionStorage flag for a known
   * alias. Returns false when the alias is missing or the flag was never set.
   */
  isEatInTakeAwayPickupOnlyByAlias(storeAlias: string): boolean {
    if (!storeAlias) {
      return false;
    }
    return (
      safeSessionStorage.getItem(
        `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Race-safe variant for callers that cannot read the current store alias
   * cheaply — e.g. `OrderService.getInitOrder()` which is invoked while the
   * NgRx `selectedStore` slice is in flux. Walks session storage for any
   * matching flag; one browser session is bound to one store, so any match
   * unambiguously identifies the current store.
   */
  isEatInTakeAwayPickupOnlyForCurrentSession(): boolean {
    const prefix = `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:`;
    return safeSessionStorage
      .keys()
      .some(
        (key) =>
          key.startsWith(prefix) && safeSessionStorage.getItem(key) === 'true',
      );
  }

  /**
   * Clear the Eat-In/Take-Away pickup-only flag for the given alias.
   * Called from `clearAllState()` — kept as a discrete method so tests can
   * exercise it independently.
   */
  clearEatInTakeAwayPickupOnlyState(storeAlias: string): void {
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:${storeAlias}`,
    );
  }

  /**
   * Clear consumption mode selected state
   */
  clearConsumptionModeState(storeAlias: string): void {
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:consumptionModeSelected:${storeAlias}`,
    );
  }

  /**
   * Clear promotion shown state
   */
  clearPromotionState(storeAlias: string): void {
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:promotionShown:${storeAlias}`,
    );
  }

  /**
   * Clear all session state for a store (useful for testing or logout)
   */
  clearAllState(storeAlias: string): void {
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:videoShown:${storeAlias}`,
    );
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:consumptionModeSelected:${storeAlias}`,
    );
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:promotionShown:${storeAlias}`,
    );
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
    );
    safeSessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:${storeAlias}`,
    );
  }

  /**
   * Helper to create ViewDecision object
   */
  private createDecision(
    viewType: InitialViewType,
    clearEverything: boolean,
    routePath: string | null,
  ): ViewDecision {
    return {
      viewType,
      clearEverything,
      routePath,
    };
  }
}
